import { useMemo } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { Box, ButtonBase, Stack, Typography, useTheme } from '@mui/material'
import { ArrowRightIcon } from 'lucide-react'

import { Card, Skeleton } from '@repo/ui'
import { SankeyChart } from 'components/analytics/SankeyChart'
import { InfoTooltip } from 'components/feedback/InfoTooltip'
import { useReferrerState } from 'hooks'
import { useWebsiteFunnel } from 'hooks/api'
import type { DayRange, WebsiteAnalyticsOverview } from 'services'

import { AUDIENCE_LABELS, formatCount, SALES_PIPELINE_HANDOFF, sourceNotice } from '../websiteAnalyticsModel'
import {
  coverageLine,
  EMPTY_POPULATION_TEXT,
  FUNNEL_CHART_HEIGHT,
  FUNNEL_TERMS,
  funnelChartData,
  inferredNote,
  SMALL_SAMPLE_BELOW,
  waitlistNote,
} from '../websiteFunnelModel'
import { Notice, SectionNote } from './shared'

/**
 * The handoff to sales (VRG-690, Spencer 2026-10-13): the funnel ends at Joined waitlist, and
 * this row says how many crossed and where they went. A navigation row (§34): border at rest,
 * brand border + ring on hover and keyboard focus. Carries its referrer (§42) so the pipeline
 * can offer a way back. The count is vera-2's own (every waitlist join in the range, by any
 * route), the same figure as the Waitlist joined tile.
 */
const SalesHandoff = ({ waitlist }: { waitlist: number | null }) => {
  const theme = useTheme()
  const referrer = useReferrerState('Website')
  return (
    <ButtonBase
      component={RouterLink}
      to={SALES_PIPELINE_HANDOFF}
      state={referrer}
      data-testid="funnel-sales-handoff"
      sx={{
        width: '100%',
        justifyContent: 'space-between',
        gap: 3,
        px: 5,
        py: 4,
        borderRadius: 2,
        border: `1px solid ${theme.palette.primary[100]}`,
        bgcolor: 'background.paper',
        textAlign: 'left',
        '&:hover, &:focus-visible': {
          borderColor: theme.palette.brand[600],
          boxShadow: `0 0 0 3px ${theme.palette.brand['600-6%']}`,
        },
      }}
    >
      <Typography variant="body2">
        <Box component="span" sx={{ fontWeight: 600 }}>
          {formatCount(waitlist)}
        </Box>{' '}
        joined the waitlist
      </Typography>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ color: theme.palette.brand[600], whiteSpace: 'nowrap' }}>
        <Typography variant="body3" sx={{ color: 'inherit', fontWeight: 600 }}>
          See the Sales pipeline
        </Typography>
        <ArrowRightIcon size={16} strokeWidth={1.5} />
      </Stack>
    </ButtonBase>
  )
}

/** The loading state, laid out as the loaded card is (§19): coverage line, chart, handoff row. */
export const WebsiteFunnelSkeleton = () => (
  <Card title="Funnel">
    <Stack spacing={3} sx={{ mt: 4 }} data-testid="funnel-skeleton">
      <Skeleton height={16} width="55%" />
      <Skeleton height={FUNNEL_CHART_HEIGHT} />
      <Skeleton height={56} />
    </Stack>
  </Card>
)

/**
 * VRG-714 (Spencer, 2026-10-16) — the website funnel as ONE Sankey of per-visitor paths:
 *
 *   Visited → {Opened Get started, Started ROI calculator, Both, Neither} → {Submitted the
 *   form, Calculated ROI, Both} → Joined waitlist, with a "Left" at each stage.
 *
 * It replaces VRG-690's four phase boxes and the Vercel / PostHog toggle. The boxes had to stop
 * at a dashed "rates restart here" line, because Vercel counts events and vera-2's waitlist is
 * not joined to a visitor; a Sankey of PEOPLE needs neither compromise. It is drawn from PostHog
 * alone (only analytics-on visitors have a path), with the waitlist joined to each person on
 * the server the way lead journeys join it. Waitlist joins from outside that population are a
 * note, never a band, so every node's in = out (pinned in the API guard spec).
 *
 * Above the chart, ONE coverage line says how much of the traffic it covers, from Vercel: visits
 * in total, the analytics-on visitors shown below, and an approximate share computed in the SAME
 * unit on both sides (person-days against Vercel visits; see `coverageLine`). Two tooltips, one
 * per side, from the glossary (Spencer: say plainly what each count can and cannot see).
 */
export const WebsiteFunnelCard = ({ overview, range }: { overview: WebsiteAnalyticsOverview; range: DayRange }) => {
  const { data, isLoading, error } = useWebsiteFunnel(range)
  const chart = useMemo(() => (data ? funnelChartData(data) : null), [data])
  const coverage = coverageLine({
    vercelVisits: overview.allVisitors.visitors,
    population: data?.population ?? null,
    todayCut: !!range.from,
  })
  const problem =
    data && data.posthog.status !== 'ok' && data.posthog.status !== 'fixture' ? sourceNotice('PostHog', data.posthog) : null
  const empty = !!data && data.population !== null && data.population.visitors === 0
  const notes = data ? [waitlistNote(data), inferredNote(data)].filter((x): x is string => !!x) : []

  return (
    <Card title="Funnel" action={<InfoTooltip term={FUNNEL_TERMS.card} />}>
      <Stack spacing={3} sx={{ mt: 4 }} data-testid="website-funnel-card">
        {isLoading ? (
          <Box data-testid="funnel-skeleton">
            <Skeleton height={16} width="55%" />
            <Box sx={{ mt: 3 }}>
              <Skeleton height={FUNNEL_CHART_HEIGHT} />
            </Box>
          </Box>
        ) : error ? (
          <Notice severity="error">Could not load the funnel. Reload the page, or check the API is running.</Notice>
        ) : (
          <>
            {/* The coverage line: everything (Vercel), then what the chart can follow (PostHog). */}
            <Stack
              direction="row"
              alignItems="center"
              flexWrap="wrap"
              columnGap={2}
              rowGap={1}
              data-testid="funnel-coverage"
            >
              <Typography variant="body3" color="text.secondary">
                {coverage.totalText}
              </Typography>
              <InfoTooltip term={FUNNEL_TERMS.allVisits} />
              <Typography variant="body3" color="text.secondary" aria-hidden>
                ·
              </Typography>
              <Typography variant="body3" color="text.secondary">
                {coverage.shownText}
                {coverage.shareText ? ` (${coverage.shareText})` : ''}
              </Typography>
              <InfoTooltip term={FUNNEL_TERMS.analyticsOnPaths} caveat={coverage.caveat} />
            </Stack>
            {problem ? <Notice severity={data?.posthog.status === 'error' ? 'error' : 'warning'}>{problem}</Notice> : null}
            {empty ? (
              <SectionNote>{EMPTY_POPULATION_TEXT}</SectionNote>
            ) : chart && data?.population ? (
              <Box data-testid="website-funnel-sankey">
                <SankeyChart
                  ariaLabel={`How visitors move from a visit to the waitlist, ${AUDIENCE_LABELS.consented.toLowerCase()}`}
                  nodes={chart.nodes}
                  links={chart.links}
                  unit="people"
                  nUnit="people"
                  smallSampleBelow={SMALL_SAMPLE_BELOW}
                  preserveOrder
                  height={FUNNEL_CHART_HEIGHT}
                />
              </Box>
            ) : null}
            {notes.map((n) => (
              <SectionNote key={n}>{n}</SectionNote>
            ))}
          </>
        )}
        <SalesHandoff waitlist={overview.vera?.waitlist ?? null} />
      </Stack>
    </Card>
  )
}
